import { CONFIGURATION_PCBA_SERIAL_FORMAT_ERROR } from "@integrator-src/manufacturing/configuration-pcba-serial";

import type {
  ConfigurationClampQcCheckResponse,
  ConfigurationOpenQualityIncidentCheckResponse,
  ConfigurationPcbaPrefixTlaCheckResponse,
  SubmitConfigurationBody,
} from "./configurationApi";
import { recordHubSupportConsoleLine } from "@/lib/installHubSupportConsoleCapture";

export const CONFIGURATION_PCBA_FORMAT_SUPPORT_EVENT = "configuration_pcba_format";
export const CONFIGURATION_CLIENT_VALIDATION_SUPPORT_EVENT = "configuration_client_validation";

/** Device identity fields attached to hub support console lines for configure failures. */
export function buildConfigurationSubmitSupportContext(
  body: SubmitConfigurationBody,
): Record<string, string | number | boolean | null> {
  return {
    finishedSku: body.finishedSku,
    batchItemId: body.batchItemId,
    buildOrderNumber: body.buildOrderNumber,
    alternateOrderId: body.alternateOrderId ?? null,
    rescanSerialFciId: body.rescanSerialFciId ?? null,
    isAlternate: body.isAlternate,
    deviceLabelSerial: body.deviceLabelSerial,
    pcbaSerial: body.pcbaSerial,
    clampSerial: body.clampSerial,
    mcuUuid: body.mcuUuid,
    childPcba: body.childPcba,
  };
}

export function buildConfigurationFormSupportContext(input: {
  formData: Record<string, string>;
  finishedSku?: string | null;
  batchItemId?: number | null;
  alternateOrderId?: number | null;
  rescanSerialFciId?: number | null;
  mcuUuid?: string | null;
}): Record<string, string | number | null> {
  return {
    finishedSku: input.finishedSku ?? null,
    batchItemId: input.batchItemId ?? null,
    alternateOrderId: input.alternateOrderId ?? null,
    rescanSerialFciId: input.rescanSerialFciId ?? null,
    deviceLabelSerial: input.formData.deviceLabelSerial?.trim() || null,
    pcbaSerial: input.formData.pcbaSerial?.trim() || null,
    clampSerial: input.formData.clampSerial?.trim() || null,
    mcuUuid: input.mcuUuid?.trim() || null,
  };
}

function configurationClientValidationEvent(field: string, message: string): string {
  if (field === "pcbaSerial" && message === CONFIGURATION_PCBA_SERIAL_FORMAT_ERROR) {
    return CONFIGURATION_PCBA_FORMAT_SUPPORT_EVENT;
  }
  return CONFIGURATION_CLIENT_VALIDATION_SUPPORT_EVENT;
}

/**
 * Feed Submit-time client validation failures into hub support ticket console_capture.
 * Call only from the submit path (once per failed Submit), never while typing serials.
 */
export function recordConfigurationClientValidationFailureForSupport(input: {
  errors: Record<string, string>;
  formData: Record<string, string>;
  finishedSku?: string | null;
  batchItemId?: number | null;
  alternateOrderId?: number | null;
  rescanSerialFciId?: number | null;
  mcuUuid?: string | null;
}): void {
  const fieldNames = Object.keys(input.errors);
  if (fieldNames.length === 0) return;

  const device = buildConfigurationFormSupportContext(input);
  for (const field of fieldNames) {
    const operator_message = input.errors[field];
    if (!operator_message) continue;
    recordHubSupportConsoleLine("error", {
      event: configurationClientValidationEvent(field, operator_message),
      field,
      rejected_value: String(input.formData[field] ?? "").trim() || null,
      operator_message,
      device,
    });
  }
}

/** Feed PCBA prefix TLA check failures into hub support ticket console_capture. */
export function recordConfigurationPcbaPrefixTlaCheckForSupport(
  check: ConfigurationPcbaPrefixTlaCheckResponse,
  context: {
    finishedSku: string;
    batchItemId?: number | null;
    alternateOrderId?: number | null;
  },
): void {
  if (check.matches_required_tla !== false && check.has_prefix_assignment !== false) {
    return;
  }

  const event =
    check.has_prefix_assignment === false
      ? "configuration_pcba_prefix_no_assignment"
      : "configuration_pcba_prefix_tla_mismatch";

  recordHubSupportConsoleLine("error", {
    event,
    operator_message: check.mismatch_message,
    finishedSku: context.finishedSku,
    batchItemId: context.batchItemId ?? null,
    alternateOrderId: context.alternateOrderId ?? null,
    pcba_serial: check.pcba_serial,
    pcba_prefix: check.pcba_prefix,
    required_tla_sku: check.required_tla_sku,
    has_prefix_assignment: check.has_prefix_assignment,
    matches_required_tla: check.matches_required_tla,
    tla_assembly_output_sku: check.tla_assembly_output_sku,
    expectation: check.expectation,
  });
}

/** Feed clamp QC check failures into hub support ticket console_capture. */
export function recordConfigurationClampQcCheckForSupport(
  check: ConfigurationClampQcCheckResponse,
  context: {
    finishedSku: string;
    batchItemId?: number | null;
    alternateOrderId?: number | null;
  },
): void {
  if (check.skipped || check.eligible !== false) {
    return;
  }

  recordHubSupportConsoleLine("error", {
    event: "configuration_clamp_qc_blocked",
    operator_message: check.block_message,
    finishedSku: context.finishedSku,
    batchItemId: context.batchItemId ?? null,
    alternateOrderId: context.alternateOrderId ?? null,
    clamp_serial: check.clamp_serial,
    eligible: check.eligible,
    skipped: check.skipped,
  });
}

/** Feed bench cable timeouts and missing unit identity into hub support ticket console_capture. */
export function recordConfigurationDeviceCommunicationFailureForSupport(input: {
  operatorMessage: string;
  rawMessage: string;
  connectorId?: string | null;
  firmware?: string | null;
  finishedSku?: string | null;
  batchItemId?: number | null;
  alternateOrderId?: number | null;
}): void {
  recordHubSupportConsoleLine("error", {
    event: "configuration_device_communication",
    operator_message: input.operatorMessage,
    raw_message: input.rawMessage,
    connector_id: input.connectorId ?? null,
    firmware: input.firmware ?? null,
    finishedSku: input.finishedSku ?? null,
    batchItemId: input.batchItemId ?? null,
    alternateOrderId: input.alternateOrderId ?? null,
  });
}

/** Feed open QC incident check failures into hub support ticket console_capture. */
export function recordConfigurationOpenQualityIncidentCheckForSupport(
  check: ConfigurationOpenQualityIncidentCheckResponse,
  context: {
    finishedSku: string;
    batchItemId?: number | null;
    alternateOrderId?: number | null;
    pcbaSerial?: string | null;
    clampSerial?: string | null;
    deviceLabelSerial?: string | null;
    serialFciId?: number | null;
  },
): void {
  if (check.skipped || check.eligible !== false) {
    return;
  }

  recordHubSupportConsoleLine("error", {
    event: "configuration_open_qc_incident_blocked",
    operator_message: check.block_message,
    finishedSku: context.finishedSku,
    batchItemId: context.batchItemId ?? null,
    alternateOrderId: context.alternateOrderId ?? null,
    incident_id: check.incident_id,
    pcba_serial: context.pcbaSerial ?? null,
    clamp_serial: context.clampSerial ?? null,
    device_label_serial: context.deviceLabelSerial ?? null,
    serial_fci_id: context.serialFciId ?? null,
    eligible: check.eligible,
    skipped: check.skipped,
  });
}
